import { useState } from "react";
import { useSelector } from "react-redux";
import styles from "./AdminShared.module.css";
import local from "./AdminTracking.module.css";
import { useAdminUI } from "./AdminUIContext";
import {
  isThisBrowserExcluded,
  setThisBrowserExcluded,
  getLastLandingSessionId,
  clearLastLanding,
} from "../../../data/utmTracking";
import { retractTrackEvent } from "../../../storage/trackDB";

const AdminTrackBrowserSetting = ({ variant = "dashboard" }) => {
  const { toast, confirm } = useAdminUI();
  const user = useSelector((state) => state.auth.user);
  const [excluded, setExcluded] = useState(() => isThisBrowserExcluded());
  const [busy, setBusy] = useState(false);
  const [canRetract, setCanRetract] = useState(
    () => Boolean(getLastLandingSessionId()),
  );

  const offerRetractVisit = async () => {
    const sessionId = getLastLandingSessionId();
    if (!sessionId) return;
    const remove = await confirm({
      title: "Remove the counted visit?",
      message: [
        "This browser is excluded. A landing from this browser may already be in Tracking — the visit that happened before you clicked Exclude.",
        "Remove that one visit? Signups stay as they are.",
      ],
      confirmLabel: "Remove visit",
      cancelLabel: "Keep the visit",
      danger: true,
    });
    if (!remove) return;
    await retractVisit(sessionId);
  };

  const retractVisit = async (sessionId) => {
    const id = sessionId || getLastLandingSessionId();
    if (!id) {
      toast.info("No visit from this browser is stored to remove.");
      return;
    }
    setBusy(true);
    try {
      const result = await retractTrackEvent(user, {
        sessionId: id,
        event: "landing",
      });
      clearLastLanding();
      setCanRetract(false);
      if (result?.deleted) {
        toast.success("Removed that visit from Tracking.");
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-tracking"));
      } else {
        toast.info("No matching visit was found to remove.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not remove the visit.",
      );
    } finally {
      setBusy(false);
    }
  };

  const apply = async (next) => {
    setThisBrowserExcluded(next);
    setExcluded(isThisBrowserExcluded());
    if (!next) {
      toast.success("This browser can be counted on the public site.");
      return;
    }
    setCanRetract(Boolean(getLastLandingSessionId()));
    toast.success("This browser is excluded from tracking.");
    await offerRetractVisit();
  };

  if (excluded) {
    return (
      <div className={local["browser-quiet"]}>
        <p>This browser is excluded from tracking.</p>
        <div className={local["browser-quiet-actions"]}>
          {canRetract && (
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={() => offerRetractVisit()}
              disabled={busy}
            >
              Remove last visit
            </button>
          )}
          <button
            type="button"
            className={styles["btn-ghost"]}
            onClick={() => apply(false)}
            disabled={busy}
          >
            Count this browser
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={local["browser-loud"]} role="status">
      <div className={local["browser-loud-copy"]}>
        <p className={local["browser-loud-kicker"]}>Action needed</p>
        <p className={local["browser-loud-title"]}>
          This browser can be tracked
        </p>
        <p className={local["browser-loud-body"]}>
          {variant === "tracking"
            ? "Public visits from this browser can add landings and change conversion. Exclude it so your own clicks stay out of Tracking. You can then choose to remove the one visit already counted."
            : "Public visits from this browser can add landings. Exclude it so your own clicks do not affect Tracking. You can then choose to remove the one visit already counted."}
        </p>
      </div>
      <button
        type="button"
        className={styles["btn-primary"]}
        onClick={() => apply(true)}
        disabled={busy}
      >
        Exclude this browser
      </button>
    </div>
  );
};

export default AdminTrackBrowserSetting;
